import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import {
  computePositionsToUse,
  computeEdgesToRender,
  computeTasksToRender,
  getNodeAnimationStateForTask
} from './utils/focusAnimationUtils';

/**
 * useFocusAnimation Hook
 * 
 * Manages the animation state machine for focus mode transitions.
 * 
 * ENTERING sequence (pan/slide first, then zoom):
 * 1. 'entering-pan' - Pan to center on selected nodes (at original positions)
 * 2. 'entering-fade-edges' - Fade out all connections
 * 3. 'entering-fade-nodes' - Fade out non-focused nodes
 * 4. 'entering-move' - Slide focused nodes to new positions + pan to follow
 * 5. 'entering-zoom' - Zoom to fit the focus layout
 * 6. 'entering-show-edges' - Fade in new connections
 * 7. null - Complete
 * 
 * EXITING sequence (zoom out first, then slide):
 * 1. 'exiting-fade-edges' - Fade out connections
 * 2. 'exiting-zoom' - Zoom out to show where nodes will return
 * 3. 'exiting-move' - Slide focused nodes back + pan to follow
 * 4. 'exiting-fade-nodes' - Fade in non-focused nodes
 * 5. 'exiting-show-edges' - Fade in connections
 * 6. null - Complete
 * 
 * TRANSITION sequence (filter changes while in focus mode):
 * 1. 'transition-fade-out' - Fade out removed nodes
 * 2. 'transition-move' - Slide remaining nodes to new positions
 * 3. 'transition-show-edges' - Fade in new connections
 * 4. null - Complete
 */
export function useFocusAnimation({
  isFocusMode,
  allFocusedTaskIds,
  nodePositions,
  displayPositions,
  tasks,
  edges,
  displayEdges,
  lastFocusPositions,
  lastFocusEdges,
  onStartPanToNodes,
  onStartPanToFocusLayout,
  onStartZoomToFocusLayout = null, // Zoom after nodes move (entering)
  onStartPanToFocusLayoutInstant = null, // Instant version for view switching
  onStartZoomOutForExit = null, // Zoom out before nodes move (exiting)
  onStartPanBack,
  instantTransitionRef = null, // When true, skip all animations (for view switching)
}) {
  // Animation phase state
  const [animPhase, setAnimPhase] = useState(null);
  
  // Track what focus mode state we've "committed" to animating toward
  // Initialize to match isFocusMode so we don't trigger animation on mount when restoring FOKUS state
  const targetFocusModeRef = useRef(isFocusMode);
  
  // Track previous focus mode state (for detecting first render after focus)
  // IMPORTANT: Initialize to isFocusMode so that when restoring FOKUS state after view switch,
  // we don't incorrectly return nodePositions in computePositionsToUse.
  // This ensures nodes are positioned at their FOKUS positions immediately on restore.
  const wasInFocusModeRef = useRef(isFocusMode);
  
  // Snapshot of focused IDs when animation started (for consistent fading)
  const focusedIdsSnapshotRef = useRef(null);
  
  // Track IDs that are being removed during transition (for fade out)
  const removingIdsRef = useRef(null);
  
  // Timers for animation phases
  const timersRef = useRef([]);
  
  // Keep track of normal positions (updated when not in focus mode and not animating)
  const lastNormalPositionsRef = useRef(nodePositions);
  if (!isFocusMode && !animPhase) {
    lastNormalPositionsRef.current = nodePositions;
  }

  // Clear all pending timers
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  // Track if we're about to start an animation
  const pendingAnimationRef = useRef(null);
  
  // Create a signature for focused IDs to detect changes
  const focusedIdsSignature = allFocusedTaskIds 
    ? Array.from(allFocusedTaskIds).sort().join(',') 
    : null;
  
  // Keep track of previous focus positions and edges for transitions
  const stableFocusPositionsRef = useRef(null);
  const stableFocusEdgesRef = useRef(null);
  const stableFocusedIdsSignatureRef = useRef(null);
  
  // Snapshot refs for transitions
  const snapshotPositionsRef = useRef(null);
  const snapshotEdgesRef = useRef(null);
  
  // Detect if the signature changed THIS render (potential transition)
  const signatureChangedThisRender = isFocusMode && 
    stableFocusedIdsSignatureRef.current !== null &&
    stableFocusedIdsSignatureRef.current !== focusedIdsSignature;
  
  // Update stable refs when appropriate
  if (isFocusMode && !animPhase && !signatureChangedThisRender) {
    if (displayPositions) {
      stableFocusPositionsRef.current = displayPositions;
    }
    if (displayEdges) {
      stableFocusEdgesRef.current = displayEdges;
    }
    if (focusedIdsSignature) {
      stableFocusedIdsSignatureRef.current = focusedIdsSignature;
    }
  }
  
  // Detect pending animation state change
  // Skip pending detection if this is an instant transition (view switching)
  const isInstantTransition = instantTransitionRef?.current === true;
  if (isFocusMode !== targetFocusModeRef.current && animPhase === null && !isInstantTransition) {
    pendingAnimationRef.current = isFocusMode ? 'entering' : 'exiting';
    if (allFocusedTaskIds && allFocusedTaskIds.size > 0) {
      focusedIdsSnapshotRef.current = allFocusedTaskIds;
    }
  } else if (animPhase !== null || isInstantTransition) {
    pendingAnimationRef.current = null;
  }
  
  // Handle focus mode changes - main state machine
  // Use useLayoutEffect to set animation state BEFORE the browser paints
  // This prevents the "flash" of wrong positions on first focus
  useLayoutEffect(() => {
    if (isFocusMode === targetFocusModeRef.current) {
      return;
    }

    const wasAnimating = animPhase !== null;
    clearTimers();
    
    // Check if this is an instant transition (view switching) - skip all animations
    const isInstant = instantTransitionRef?.current === true;

    if (isFocusMode) {
      // ENTERING FOCUS MODE
      targetFocusModeRef.current = true;
      focusedIdsSnapshotRef.current = allFocusedTaskIds;
      wasInFocusModeRef.current = true; // Mark that we've been in focus mode
      
      if (isInstant) {
        // INSTANT: Skip all animations, go directly to final state
        setAnimPhase(null);
        pendingAnimationRef.current = null; // Clear pending state
        focusedIdsSnapshotRef.current = allFocusedTaskIds; // Set snapshot for consistency
        // Update stable refs to prevent transition effect from firing
        if (displayPositions) {
          stableFocusPositionsRef.current = displayPositions;
        }
        if (displayEdges) {
          stableFocusEdgesRef.current = displayEdges;
        }
        if (focusedIdsSignature) {
          stableFocusedIdsSignatureRef.current = focusedIdsSignature;
        }
        // NOTE: We don't call onStartPanToFocusLayoutInstant here because
        // displayPositions might not be ready yet (it's computed in useMemo).
        // The deferred positioning is handled by useFocusModeIntegration's useLayoutEffect
        // which watches for when positions become available.
        
        // Clear the instant flag after processing
        if (instantTransitionRef) instantTransitionRef.current = false;
        return;
      }
      
      setAnimPhase('entering-pan');
      onStartPanToNodes?.(allFocusedTaskIds);
      
      const fadeEdgesDelay = wasAnimating ? 200 : 350;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('entering-fade-edges');
      }, fadeEdgesDelay));
      
      const fadeNodesDelay = fadeEdgesDelay + 120;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('entering-fade-nodes');
      }, fadeNodesDelay));
      
      // Slide nodes to new positions + pan to follow
      const moveDelay = fadeNodesDelay + 150;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('entering-move');
        onStartPanToFocusLayout?.();
      }, moveDelay));
      
      // Zoom to fit after nodes have moved
      const zoomDelay = moveDelay + 400; // Wait for slide animation
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('entering-zoom');
        onStartZoomToFocusLayout?.();
      }, zoomDelay));
      
      const showEdgesDelay = zoomDelay + 300; // Wait for zoom animation
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('entering-show-edges');
      }, showEdgesDelay));
      
      const endDelay = showEdgesDelay + 200;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase(null);
      }, endDelay));
      
    } else {
      // EXITING FOCUS MODE
      targetFocusModeRef.current = false;
      wasInFocusModeRef.current = false; // Reset so next focus entry is detected as "first"
      
      if (isInstant) {
        // INSTANT: Skip all animations, go directly to final state
        setAnimPhase(null);
        focusedIdsSnapshotRef.current = null;
        pendingAnimationRef.current = null; // Clear pending state
        // CRITICAL: Clear stable refs so the transition effect doesn't fire on next entry
        stableFocusedIdsSignatureRef.current = null;
        stableFocusPositionsRef.current = null;
        stableFocusEdgesRef.current = null;
        // Clear the instant flag after processing
        if (instantTransitionRef) instantTransitionRef.current = false;
        return;
      }
      
      if (!focusedIdsSnapshotRef.current) {
        focusedIdsSnapshotRef.current = allFocusedTaskIds;
      }
      
      setAnimPhase('exiting-fade-edges');
      
      // Zoom out FIRST to show where nodes will return to
      const zoomDelay = wasAnimating ? 100 : 120;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('exiting-zoom');
        // Use snapshot IDs since allFocusedTaskIds may be cleared by now
        onStartZoomOutForExit?.(focusedIdsSnapshotRef.current || allFocusedTaskIds);
      }, zoomDelay));
      
      // Then slide nodes back to original positions
      const moveDelay = zoomDelay + 350; // Wait for zoom animation
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('exiting-move');
        // Pan to follow nodes as they return
        onStartPanBack?.(focusedIdsSnapshotRef.current || allFocusedTaskIds);
      }, moveDelay));
      
      const fadeNodesDelay = moveDelay + 400;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('exiting-fade-nodes');
      }, fadeNodesDelay));
      
      const showEdgesDelay = fadeNodesDelay + 150;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase('exiting-show-edges');
      }, showEdgesDelay));
      
      const endDelay = showEdgesDelay + 200;
      timersRef.current.push(setTimeout(() => {
        setAnimPhase(null);
        focusedIdsSnapshotRef.current = null;
        // CRITICAL: Clear stable refs so the transition effect doesn't fire on next entry
        stableFocusedIdsSignatureRef.current = null;
        stableFocusPositionsRef.current = null;
        stableFocusEdgesRef.current = null;
      }, endDelay));
    }

    return clearTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocusMode, clearTimers]);

  // Handle TRANSITIONS between different focus states
  // This should ONLY run when changing focus targets while ALREADY in focus mode
  // NOT when entering or exiting focus mode
  useEffect(() => {
    // Skip if not in focus mode, or if an animation is in progress
    // Also skip if we JUST entered focus mode (targetFocusModeRef was just set to true)
    // The wasInFocusModeRef check ensures we only run for transitions, not initial entry
    if (!isFocusMode || !targetFocusModeRef.current || animPhase !== null) {
      return;
    }
    
    // CRITICAL: Skip if we're in the middle of entering focus mode
    // The entering animation sets animPhase, but there's a brief moment where
    // this useEffect runs before the state update propagates
    // Check if we have stable focus data from a PREVIOUS focus session
    if (!stableFocusedIdsSignatureRef.current) {
      return;
    }
    
    const prevSignature = stableFocusedIdsSignatureRef.current;
    
    if (!prevSignature || prevSignature === focusedIdsSignature) {
      return;
    }
    
    // Calculate which IDs are being removed
    const prevIds = prevSignature.split(',').filter(Boolean);
    const newIds = focusedIdsSignature?.split(',').filter(Boolean) || [];
    const removingIds = new Set();
    
    prevIds.forEach(id => {
      if (!newIds.includes(id)) {
        removingIds.add(id);
      }
    });
    
    if (removingIds.size === 0) {
      return;
    }
    
    removingIdsRef.current = removingIds;
    snapshotPositionsRef.current = stableFocusPositionsRef.current;
    snapshotEdgesRef.current = stableFocusEdgesRef.current;
    
    clearTimers();
    
    setAnimPhase('transition-fade-out');
    
    const moveDelay = 220;
    timersRef.current.push(setTimeout(() => {
      setAnimPhase('transition-move');
      onStartPanToFocusLayout?.();
    }, moveDelay));
    
    const showEdgesDelay = moveDelay + 420;
    timersRef.current.push(setTimeout(() => {
      setAnimPhase('transition-show-edges');
    }, showEdgesDelay));
    
    const endDelay = showEdgesDelay + 200;
    timersRef.current.push(setTimeout(() => {
      setAnimPhase(null);
      removingIdsRef.current = null;
      snapshotPositionsRef.current = null;
      snapshotEdgesRef.current = null;
      stableFocusedIdsSignatureRef.current = focusedIdsSignature;
    }, endDelay));
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedIdsSignature, isFocusMode, animPhase, clearTimers, onStartPanToFocusLayout]);

  // Derived state
  const isAnimatingIntoFocus = animPhase?.startsWith('entering') || false;
  const isAnimatingOutOfFocus = animPhase?.startsWith('exiting') || false;
  const isTransitioning = animPhase?.startsWith('transition') || false;
  const isAnimating = animPhase !== null;
  const isPendingExit = pendingAnimationRef.current === 'exiting';
  
  // CRITICAL: Immediately show/hide animation blocker via DOM
  // useLayoutEffect ensures this happens synchronously before paint
  useLayoutEffect(() => {
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker) {
      blocker.style.display = isAnimating ? 'block' : '';
    }
  }, [isAnimating]);

  // Get effective focused IDs (use snapshot during animation)
  const effectiveFocusedIds = useMemo(() => {
    if (isAnimating && focusedIdsSnapshotRef.current) {
      return focusedIdsSnapshotRef.current;
    }
    return allFocusedTaskIds;
  }, [isAnimating, allFocusedTaskIds]);

  // Computed positions
  // Track wasInFocusMode for this render
  const wasInFocusMode = wasInFocusModeRef.current;
  
  const positionsToUse = useMemo(() => {
    return computePositionsToUse({
      animPhase,
      tasks,
      effectiveFocusedIds,
      displayPositions,
      nodePositions,
      lastFocusPositions,
      snapshotPositions: snapshotPositionsRef.current,
      pendingAnimation: pendingAnimationRef.current,
      isFocusMode,
      wasInFocusMode
    });
  }, [animPhase, tasks, effectiveFocusedIds, displayPositions, nodePositions, lastFocusPositions, isFocusMode, wasInFocusMode]);

  // Computed edges
  const edgesToRender = useMemo(() => {
    return computeEdgesToRender({
      animPhase,
      edges,
      displayEdges,
      effectiveFocusedIds,
      allFocusedTaskIds,
      lastFocusEdges,
      snapshotEdges: snapshotEdgesRef.current,
      pendingAnimation: pendingAnimationRef.current
    });
  }, [animPhase, edges, displayEdges, effectiveFocusedIds, allFocusedTaskIds, lastFocusEdges]);

  // Computed tasks to render
  // PERF: After fade-out, we stop rendering non-focused nodes entirely
  const tasksToRender = useMemo(() => {
    return computeTasksToRender({
      isAnimating,
      isTransitioning,
      animPhase,
      tasks,
      allFocusedTaskIds,
      removingIds: removingIdsRef.current,
      isPending: pendingAnimationRef.current !== null
    });
  }, [isAnimating, isTransitioning, animPhase, tasks, allFocusedTaskIds]);

  // Helper to get node animation state
  const getNodeAnimationState = useCallback((taskId) => {
    return getNodeAnimationStateForTask({
      taskId,
      animPhase,
      effectiveFocusedIds,
      allFocusedTaskIds,
      removingIds: removingIdsRef.current,
      pendingAnimation: pendingAnimationRef.current
    });
  }, [animPhase, effectiveFocusedIds, allFocusedTaskIds]);
  
  return {
    currentAnimPhase: animPhase,
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus: isAnimatingOutOfFocus || isPendingExit,
    isTransitioning,
    isAnimating,
    effectiveFocusedIds,
    positionsToUse,
    edgesToRender,
    tasksToRender,
    getNodeAnimationState,
  };
}
     